import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CookieConsentService } from './cookie-consent.service';

export interface AdSlotConfig {
  key: string;
  width: number;
  height: number;
  format?: 'iframe' | 'banner' | string;
  params?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class AdService {
  private renderer: Renderer2;
  private renderedSlots = new WeakSet<HTMLElement>();
  private scriptReadySubject = new BehaviorSubject<boolean>(false);
  private placeholderImages = ['pow.png', 'spot.png', 'jones.png'];

  scriptReady$: Observable<boolean> = this.scriptReadySubject.asObservable();
  adsConsent$: Observable<boolean>;

  constructor(
    rendererFactory: RendererFactory2,
    private cookieConsentService: CookieConsentService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.adsConsent$ = this.cookieConsentService.adsConsent$;
  }

  canLoadAds(): boolean {
    return this.cookieConsentService.hasAdsConsent();
  }

  async loadAdSlot(container: HTMLElement, config: AdSlotConfig): Promise<boolean> {
    if (!container || !this.canLoadAds() || typeof window === 'undefined') {
      return false;
    }

    if (this.renderedSlots.has(container)) {
      return true;
    }

    container.innerHTML = '';

    const atOptions = {
      key: config.key,
      format: config.format ?? 'iframe',
      height: config.height,
      width: config.width,
      params: config.params ?? {}
    };

    (window as any).atOptions = atOptions;

    const script = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.src = `//www.highperformanceformat.com/${config.key}/invoke.js`;

    const loadPromise = new Promise<boolean>(resolve => {
      script.onload = () => {
        this.scriptReadySubject.next(true);
        resolve(true);
      };
      script.onerror = () => {
        this.scriptReadySubject.next(false);
        this.renderedSlots.delete(container);
        resolve(false);
      };
    });

    this.renderer.appendChild(container, script);
    this.renderedSlots.add(container);

    return loadPromise;
  }

  clearSlot(container?: HTMLElement): void {
    if (!container) {
      return;
    }

    container.innerHTML = '';
    this.renderedSlots.delete(container);
    this.scriptReadySubject.next(false);
  }

  getRandomPlaceholder(): string | null {
    if (!this.placeholderImages.length) {
      return null;
    }

    const folder = environment.adPlaceholderFolder || '/assets/ads';
    const normalizedFolder = folder.endsWith('/') ? folder.slice(0, -1) : folder;
    const randomIndex = Math.floor(Math.random() * this.placeholderImages.length);

    return `${normalizedFolder}/${this.placeholderImages[randomIndex]}`;
  }
}
